import type { ComboboxOption } from "@/components/ui/combobox";
import { stageText } from "@/lib/contacts";
import { kindTerm } from "@/lib/dispositions";
import { humanize } from "@/lib/lead-card";
import type { Disposition, LeadCadenceMembership } from "@/lib/types";
import { formatDateTime } from "@/lib/utils";

/**
 * What a cadence does with a lead when it runs out with nobody reached (ADR-155 in platform): the
 * outcome it sets, as the system, with everything that outcome does when a person sets it.
 * Running out is the calls used up, the last day passed, or the last phase over; a rep's outcome,
 * a wrong number, do-not-call or the list letting go of the lead is not.
 */

/** Whether a cadence may not set this outcome: it would put the lead on the do-not-call list. The
 *  server's own rule (`dispositions.sets_dnc` in platform, which refuses it as the cadence is saved):
 *  the outcome moves the lead to the `dnc` stage, as every compliance outcome does. The slug says
 *  nothing. */
export function endsOnDoNotCall(d: Pick<Disposition, "category" | "stage">): boolean {
  return d.category === "compliance" || d.stage === "dnc";
}

/** The group of an outcome the catalog no longer has, last in the list. */
export const GONE_GROUP = "No longer one of your outcomes";

/** The stop card's list, searched and scrolled (`ComboboxList`), with picking nothing as its own
 *  row: the tenant's active outcomes grouped by where they send the lead, "To No response" or
 *  "Keeps the stage", in catalog order within a group. The groups that hold an outcome for nobody
 *  reached come first, as a cadence that ran out reached nobody; otherwise they come in the order
 *  they first appear.
 *  Do-not-call outcomes are left out (`endOutcomesLeftOut` says so under the list): core-api refuses
 *  one as the cadence is saved. The saved one stays, last, when the catalog no longer has it, so the
 *  card can say what the cadence names. */
export function endOutcomeOptions(catalog: readonly Disposition[], current: string): ComboboxOption[] {
  const groups: string[] = [];
  const byGroup = new Map<string, ComboboxOption[]>();
  for (const d of catalog) {
    if (!d.active || endsOnDoNotCall(d)) continue;
    const group = d.stage ? `To ${stageText(d.stage).label}` : "Keeps the stage";
    if (!byGroup.has(group)) {
      groups.push(group);
      byGroup.set(group, []);
    }
    byGroup.get(group)!.push({
      value: d.slug,
      label: d.label,
      hint: kindTerm(d.kind).label + (d.requires_note ? " · needs a note" : ""),
      group,
      keywords: group,
    });
  }
  const nobodyReached = new Set(
    catalog.filter((d) => d.active && d.category === "no_contact" && !endsOnDoNotCall(d)).map((d) => d.slug),
  );
  const first = (g: string) => (byGroup.get(g)!.some((o) => nobodyReached.has(o.value)) ? 0 : 1);
  const ordered = groups.map((g, i) => ({ g, i })).sort((a, b) => first(a.g) - first(b.g) || a.i - b.i);
  const options = ordered.flatMap(({ g }) => byGroup.get(g)!);
  if (current !== "" && !options.some((o) => o.value === current)) {
    options.push({
      value: current,
      label: humanize(current) ?? current,
      hint: "Nothing is set while the cadence names it",
      group: GONE_GROUP,
    });
  }
  return options;
}

/** How many of the tenant's active outcomes the list leaves out because they mark a lead
 *  do-not-call. */
export function endOutcomesLeftOut(catalog: readonly Disposition[]): number {
  return catalog.filter((d) => d.active && endsOnDoNotCall(d)).length;
}

/** The outcome's name for the card, from the catalog when it has it. */
export function endOutcomeLabel(slug: string, catalog?: readonly Pick<Disposition, "slug" | "label">[]): string {
  return catalog?.find((d) => d.slug === slug)?.label ?? humanize(slug) ?? slug;
}

const RESULT_WORDS: Record<string, string> = {
  moved_on: "not set: the lead had moved to another stage by then",
  not_offered: "not set: that outcome is not offered here",
  exit: "not set: the lead is in an end stage",
  no_lead: "not set: the lead is gone",
};

/** The Follow-up card's line about running out: what the cadence will set, or what it set the
 *  last time it ran out on this row. Null when the cadence names nothing and never did. */
export function endOutcomeLine(m: Pick<LeadCadenceMembership, "on_end_outcome" | "on_end_outcome_label" | "ran_out">): string | null {
  const ran = m.ran_out;
  if (ran) {
    const name = ran.outcome_label ?? humanize(ran.outcome) ?? ran.outcome;
    const when = formatDateTime(ran.requested_at);
    if (ran.result === null) return `Ran out ${when}: setting ${name}`;
    if (ran.result === "recorded") return `Ran out ${when}: set ${name}`;
    return `Ran out ${when}: ${name} ${RESULT_WORDS[ran.result] ?? `not set (${ran.result})`}`;
  }
  if (m.on_end_outcome) {
    const name = m.on_end_outcome_label ?? humanize(m.on_end_outcome) ?? m.on_end_outcome;
    return `When it runs out: sets ${name}`;
  }
  return null;
}
